import { AudioPlayer } from '@/components/audio-player'
import { Button } from '@/components/ui/button'
import type { Message } from '@/interfaces/message'
import { cn } from '@/lib/utils'
import {
    listAgentTicketsQuery,
    listDeskTicketsQuery,
    listFinishedTicketsQuery,
    listPendingTicketsQuery,
    listTicketMessagesQuery,
} from '@/queries/ticket.query'
import {
    selectCurrentTicketId,
    selectMessageStatuses,
    selectRetryMessage,
    useChatStore,
} from '@/stores/chat.store'
import { getMediaUrlByMediaKey } from '@/utils/get-mediaurl-by-mediakey'
import { useQuery, useSuspenseQuery } from '@tanstack/react-query'
import { format } from 'date-fns'
import {
    AlertCircle,
    Check,
    CheckCheck,
    Clock,
    Download,
    FileText,
    LayoutTemplate,
    RefreshCw,
} from 'lucide-react'
import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import Lightbox from 'yet-another-react-lightbox'
import 'yet-another-react-lightbox/styles.css'
import { getCurrentChatTicket } from '../utils/ticket-routing'
import { ChatMessagesLoading } from './chat-messages-loading'

export function ChatMessages() {
  const currentTicketId = useChatStore(selectCurrentTicketId)
  const messageStatuses = useChatStore(selectMessageStatuses)
  const retryMessage = useChatStore(selectRetryMessage)
  const pendingMessagesMap = useChatStore((state) => state.pendingMessages)
  const { data: agentTickets } = useSuspenseQuery(listAgentTicketsQuery)
  const { data: deskTickets } = useSuspenseQuery(listDeskTicketsQuery)
  const { data: pendingTickets } = useSuspenseQuery(listPendingTicketsQuery)
  const { data: finishedTickets } = useSuspenseQuery(listFinishedTicketsQuery)
  const currentTicket = getCurrentChatTicket(
    currentTicketId,
    agentTickets,
    deskTickets,
    pendingTickets,
    finishedTickets
  )

  const { data: apiMessages, isLoading } = useQuery({
    ...listTicketMessagesQuery(currentTicket?.id || 0),
    enabled: !!currentTicket,
  })

  const messages = useMemo(() => {
    if (!apiMessages || !currentTicket) return apiMessages

    const failedMessages: Message[] = []

    pendingMessagesMap.forEach((pendingMsg, tempId) => {
      if (pendingMsg.ticketId !== currentTicket.id) return
      if (pendingMsg.status !== 'failed') return

      const alreadyExists = apiMessages.some((m) => m.id === tempId)
      if (alreadyExists) return

      const formData = pendingMsg.formData
      let content = ''
      let type = 'text'
      let caption = ''

      if (formData instanceof FormData) {
        content = (formData.get('content') as string) || ''
        type = (formData.get('type') as string) || 'text'
        caption = (formData.get('caption') as string) || ''
      } else {
        content = formData.content || ''
        type = formData.type || 'text'
        caption = formData.caption || ''
      }

      failedMessages.push({
        id: tempId,
        read: false,
        mediaUrl: '',
        mediaKey: '',
        mediaType: type as Message['mediaType'],
        filename: '',
        type: type as Message['type'],
        from: '',
        fromMe: true,
        to: '',
        mimetype: '',
        timestamp: pendingMsg.timestamp,
        caption,
        mediaDuration: 0,
        mediaHeight: 0,
        mediaWidth: 0,
        mediaThumbnailUrl: '',
        quotedMsgId: '',
        authorId: '',
        contactId: currentTicket.contact.id,
        whatsappId: currentTicket.whatsappId,
        ticketId: currentTicket.id,
        content,
        createdAt: pendingMsg.timestamp,
        updatedAt: pendingMsg.timestamp,
      })
    })

    return [...apiMessages, ...failedMessages]
  }, [apiMessages, currentTicket, pendingMessagesMap])

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [lightboxIndex, setLightboxIndex] = useState(0)
  const [lightboxSlides, setLightboxSlides] = useState<{ src: string }[]>([])

  const orderedMessages = useMemo(() => {
    if (!messages) return []
    return [...messages].sort((a, b) => {
      const aDate = parseMessageDate(a)?.getTime() ?? 0
      const bDate = parseMessageDate(b)?.getTime() ?? 0
      return aDate - bDate
    })
  }, [messages])

  const currentMessages = useMemo(() => {
    if (!orderedMessages.length) return null
    return orderedMessages.reduce((acc: Record<string, Message[]>, obj) => {
      const createdAtDate = parseMessageDate(obj)
      const key = createdAtDate
        ? format(createdAtDate, 'd MMM, yyyy')
        : 'Sem data'

      if (obj.type === 'reaction') return acc
      if (!acc[key]) {
        acc[key] = []
      }

      acc[key].push(obj)
      return acc
    }, {})
  }, [orderedMessages])

  const openLightbox = (imageSrc: string, allImages: string[]) => {
    const slides = allImages.map((src) => ({ src }))
    const index = allImages.indexOf(imageSrc)
    setLightboxSlides(slides)
    setLightboxIndex(index >= 0 ? index : 0)
    setLightboxOpen(true)
  }

  useEffect(() => {
    if (!isLoading && orderedMessages.length && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [orderedMessages, isLoading, currentTicketId])

  return (
    <div className='flex size-full flex-1'>
      <div className='chat-text-container relative -mr-2 flex flex-1 flex-col overflow-y-hidden sm:-mr-4'>
        <div className='chat-flex flex h-40 w-full flex-grow flex-col justify-start gap-4 overflow-y-auto py-2 pb-0 pr-2 pt-[90px] sm:pr-4'>
          {isLoading ? (
            <ChatMessagesLoading />
          ) : (
            currentMessages &&
            Object.keys(currentMessages).map((key) => (
              <Fragment key={key}>
                <div className='text-center text-xs'>{key}</div>
                {groupImageSequences(currentMessages[key]).map(
                  (item, index) => {
                    const meta = isImageGroup(item)
                      ? item.messages[item.messages.length - 1]
                      : item
                    const messageId = String(meta.id)
                    const messageStatus =
                      messageStatuses.get(messageId) || 'sent'
                    const isPending = messageStatus === 'pending'
                    const isFailed = messageStatus === 'failed'

                    return (
                      <div
                        key={`${meta.id}-${meta.createdAt}-${index}`}
                        className={cn(
                          'flex flex-col gap-1',
                          meta.fromMe
                            ? 'items-end self-end'
                            : 'items-start self-start'
                        )}
                      >
                        <div className='flex items-center gap-1'>
                          {isFailed && meta.fromMe && (
                            <Button
                              size='icon'
                              variant='ghost'
                              className='h-6 w-6'
                              onClick={() => retryMessage(messageId)}
                            >
                              <RefreshCw size={14} />
                            </Button>
                          )}
                          <div
                            className={cn(
                              'chat-box max-w-[85vw] break-words px-3 py-2 shadow-sm sm:max-w-96',
                              meta.fromMe
                                ? 'rounded-[16px_16px_0_16px] bg-primary text-primary-foreground'
                                : 'rounded-[16px_16px_16px_0] border border-border bg-white text-foreground dark:bg-card'
                            )}
                          >
                            {isImageGroup(item) ? (
                              <ImageGroup
                                group={item}
                                onImageClick={openLightbox}
                              />
                            ) : (
                              <MessageContent
                                message={meta}
                                onImageClick={openLightbox}
                              />
                            )}
                          </div>
                        </div>
                        <div className='flex items-center gap-1 text-xs text-muted-foreground'>
                          <span>{formatMessageTime(meta)}</span>
                          {meta.fromMe &&
                            (isFailed ? (
                              <AlertCircle size={14} className='text-red-500' />
                            ) : isPending ? (
                              <Clock size={14} className='animate-pulse' />
                            ) : meta.read ? (
                              <CheckCheck size={14} className='text-blue-500' />
                            ) : (
                              <Check size={14} />
                            ))}
                        </div>
                      </div>
                    )
                  }
                )}
              </Fragment>
            ))
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>
      <Lightbox
        open={lightboxOpen}
        close={() => setLightboxOpen(false)}
        slides={lightboxSlides}
        index={lightboxIndex}
        on={{ view: ({ index }) => setLightboxIndex(index) }}
        carousel={{ finite: true }}
      />
    </div>
  )
}

function trimContent(text: string | null | undefined): string {
  if (!text) return ''
  return text.trim()
}

function MessageContent({
  message,
  onImageClick,
}: {
  message: Message
  onImageClick: (imageSrc: string, allImages: string[]) => void
}) {
  const mediaSource = getMessageMediaSource(message)

  if (message.type === 'text') {
    return (
      <p className='whitespace-pre-wrap break-words'>
        {trimContent(message.content)}
      </p>
    )
  }

  if (message.type === 'image') {
    return (
      <SingleImage
        message={message}
        mediaSource={mediaSource}
        onImageClick={onImageClick}
      />
    )
  }

  if (message.type === 'sticker') {
    return (
      <div className='flex flex-col items-center gap-2'>
        {mediaSource ? (
          <img
            src={mediaSource}
            alt={message.caption || 'sticker'}
            className='max-h-40 w-full max-w-[200px] object-contain'
          />
        ) : (
          <p className='text-sm text-muted-foreground'>Sticker indisponível</p>
        )}
        {message.caption && (
          <p className='text-xs text-muted-foreground'>
            {trimContent(message.caption)}
          </p>
        )}
      </div>
    )
  }

  if (message.type === 'audio' || message.type === 'ptt') {
    return mediaSource ? (
      <AudioPlayer audioUrl={mediaSource} className='w-full' />
    ) : (
      <p className='text-sm text-muted-foreground'>Áudio indisponível</p>
    )
  }

  if (message.type === 'video') {
    return (
      <div className='flex flex-col gap-2'>
        {mediaSource ? (
          <video
            src={mediaSource}
            poster={message.mediaThumbnailUrl || undefined}
            controls
            className='max-h-80 rounded-md border border-border object-contain'
          />
        ) : (
          <p className='text-sm text-muted-foreground'>Vídeo indisponível</p>
        )}
        {message.caption && (
          <p className='text-sm'>{trimContent(message.caption)}</p>
        )}
      </div>
    )
  }

  if (message.type === 'document') {
    return <DocumentAttachment message={message} mediaSource={mediaSource} />
  }

  if (message.type === 'template') {
    return <TemplateMessage message={message} />
  }

  return <p className='text-sm text-muted-foreground'>Conteúdo não suportado</p>
}

function parseMessageDate(message: Message) {
  const date = new Date(message.createdAt || message.timestamp)
  return Number.isNaN(date.getTime()) ? null : date
}

function formatMessageTime(message: Message) {
  const date = parseMessageDate(message)
  return date ? format(date, 'HH:mm') : '--:--'
}

function getMessageMediaSource(message: Message) {
  if (message.mediaUrl) return message.mediaUrl
  if (message.mediaKey) {
    try {
      return getMediaUrlByMediaKey(message.mediaKey)
    } catch {
      return ''
    }
  }
  if (message.mediaThumbnailUrl) return message.mediaThumbnailUrl
  return ''
}

type ImageGroup = {
  kind: 'image-group'
  messages: Message[]
  fromMe: boolean
}

type MessageItem = Message | ImageGroup

function isImageGroup(item: MessageItem): item is ImageGroup {
  return (item as ImageGroup).kind === 'image-group'
}

function groupImageSequences(messages: Message[]): MessageItem[] {
  const grouped: MessageItem[] = []
  let buffer: Message[] = []

  const flush = () => {
    if (buffer.length) {
      grouped.push({
        kind: 'image-group',
        messages: buffer,
        fromMe: buffer[0]?.fromMe ?? false,
      })
      buffer = []
    }
  }

  for (const message of messages) {
    const isImage = message.type === 'image'
    if (isImage) {
      if (buffer.length === 0 || buffer[0].fromMe === message.fromMe) {
        buffer.push(message)
        continue
      }
      flush()
      buffer.push(message)
      continue
    }

    flush()
    grouped.push(message)
  }

  flush()
  return grouped
}

function ImageGroup({
  group,
  onImageClick,
}: {
  group: ImageGroup
  onImageClick: (imageSrc: string, allImages: string[]) => void
}) {
  const images = group.messages.slice(0, 4)
  const remaining = group.messages.length - images.length
  const caption = group.messages.find((item) => item.caption)?.caption
  const allImageSources = group.messages
    .map((m) => getMessageMediaSource(m))
    .filter(Boolean)

  return (
    <div className='flex flex-col gap-2'>
      <div
        className={cn(
          'grid gap-1',
          images.length === 1 ? 'grid-cols-1' : 'grid-cols-2'
        )}
      >
        {images.map((message, index) => {
          const source = getMessageMediaSource(message)
          const showOverlay = remaining > 0 && index === images.length - 1

          return (
            <div
              key={message.id}
              className='relative h-32 cursor-pointer overflow-hidden rounded-md border border-border/60 bg-muted/40 transition-opacity hover:opacity-90'
              onClick={() => source && onImageClick(source, allImageSources)}
            >
              {source ? (
                <img
                  src={source}
                  alt={message.caption}
                  className='h-full w-full object-cover'
                />
              ) : (
                <div className='flex h-full w-full items-center justify-center text-xs text-muted-foreground'>
                  Imagem indisponível
                </div>
              )}
              {showOverlay && (
                <div className='absolute inset-0 flex items-center justify-center bg-black/50 text-lg font-semibold text-white'>
                  +{remaining}
                </div>
              )}
            </div>
          )
        })}
      </div>
      {caption && (
        <p className='text-xs text-muted-foreground'>{trimContent(caption)}</p>
      )}
    </div>
  )
}

function SingleImage({
  message,
  mediaSource,
  onImageClick,
}: {
  message: Message
  mediaSource: string
  onImageClick: (imageSrc: string, allImages: string[]) => void
}) {
  return (
    <div className='flex flex-col gap-2'>
      {mediaSource ? (
        <img
          src={mediaSource}
          alt={message.caption}
          className='max-h-80 w-full cursor-pointer rounded-md border border-border/60 object-cover transition-opacity hover:opacity-90'
          onClick={() => onImageClick(mediaSource, [mediaSource])}
        />
      ) : (
        <p className='text-sm text-muted-foreground'>Imagem indisponível</p>
      )}
      {message.caption && (
        <p className='text-sm'>{trimContent(message.caption)}</p>
      )}
    </div>
  )
}

function DocumentAttachment({
  message,
  mediaSource,
}: {
  message: Message
  mediaSource: string
}) {
  if (!mediaSource) {
    return (
      <p className='text-sm text-muted-foreground'>Documento indisponível</p>
    )
  }

  return (
    <a
      href={mediaSource}
      download={message.filename || 'documento'}
      target='_blank'
      rel='noreferrer'
      className='flex items-center gap-3 rounded-md border border-border/70 bg-background/70 p-3 text-foreground transition hover:border-primary hover:shadow-sm'
    >
      <FileText size={18} className='text-muted-foreground' />
      <div className='min-w-0 flex-1'>
        <p className='truncate text-sm font-semibold'>
          {message.filename || 'Documento'}
        </p>
        <p className='text-xs text-muted-foreground'>Download obrigatório</p>
      </div>
      <Download size={16} className='text-muted-foreground' />
    </a>
  )
}

function TemplateMessage({ message }: { message: Message }) {
  return (
    <div className='flex items-start gap-3 rounded-md border border-border/70 bg-background/70 p-3 shadow-sm'>
      <div className='rounded-md bg-primary/10 p-2 text-primary'>
        <LayoutTemplate size={16} />
      </div>
      <div className='min-w-0 space-y-1'>
        <p className='truncate text-sm font-semibold'>
          {trimContent(message.content) || 'Template'}
        </p>
        {message.caption && (
          <p className='whitespace-pre-wrap break-words text-xs text-muted-foreground'>
            {trimContent(message.caption)}
          </p>
        )}
      </div>
    </div>
  )
}
